import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import cn from "classnames";
import { defineMessages, useIntl } from "react-intl";

import { Lead } from "@mapmycustomers/shared/types/entity";
import { Button } from "@mapmycustomers/ui";

import DuplicateWarningIcon from "@app/component/DuplicateWarningIcon";
import { setLeadAnnotation, showDuplicates } from "@app/component/leadFinder/store/actions";
import { getLeadAnnotation } from "@app/component/leadFinder/store/selectors";
import LeadFinderItemActions from "@app/component/LeadItem/components/LeadFinderItemActions";
import LeadItemHours from "@app/component/LeadItem/components/LeadItemHours";
import Interactive from "@app/component/map/core/controls/Interactive";
import OverlayView from "@app/component/map/core/primitives/OverlayView";
import useMap from "@app/component/map/FeaturedMap/context/useMap";
import { RootState } from "@app/store/rootReducer";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { leadIdGetter } from "@app/util/map/idGetters";

import styles from "./LeadAnnotation.module.scss";

interface Props {
  leads: Lead[];
  onToggleSelect: (placeId: Lead["place_id"], value: boolean) => void;
  selectedPlaceIds: Set<Lead["place_id"]>;
  zIndex?: number;
}

const messages = defineMessages({
  selectThisLead: {
    id: "leadFinder.leadAnnotation.selectThisLead",
    defaultMessage: "Select this lead",
    description: "Select this lead - button title",
  },
  undoSelection: {
    id: "leadFinder.leadAnnotation.undoSelection",
    defaultMessage: "Undo selection",
    description: "Undo selection - button title",
  },
});

const LeadAnnotation = ({
  annotation,
  leads,
  onSetAnnotation,
  onToggleSelect,
  onWarningClick,
  selectedPlaceIds,
  zIndex = 9001, // guaranteed to override sidebar and navbar
}: Props & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const analytics = useExtendedAnalytics("Lead annotation", useAnalytics());
  const intl = useIntl();
  const { map } = useMap();

  const lead = useMemo(() => {
    if (annotation) {
      return leads?.find((lead) => leadIdGetter(lead) === annotation);
    }

    return undefined;
  }, [annotation, leads]);

  const position: google.maps.LatLngLiteral | undefined = useMemo(() => {
    if (lead?.geometry) {
      return lead.geometry.location;
    }
  }, [lead]);

  const handleClearPosition = useCallback(() => {
    if (lead) {
      onSetAnnotation(undefined);
    }
  }, [lead, onSetAnnotation]);

  const handleSelect = useCallback(() => {
    if (lead) {
      onToggleSelect(leadIdGetter(lead), true);
      analytics.clicked(["Select lead"]);
    }
  }, [analytics, lead, onToggleSelect]);

  const handleUnselect = useCallback(() => {
    if (lead) {
      onToggleSelect(leadIdGetter(lead), false);
      analytics.clicked(["Unselect lead"]);
    }
  }, [analytics, lead, onToggleSelect]);

  const isVisible = map && position && lead;
  const isSelected = annotation ? selectedPlaceIds.has(annotation) : false;

  return isVisible ? (
    <AnalyticsContext.Provider value={analytics}>
      <OverlayView
        arrowClassName={styles.arrow}
        borderClassName={styles.border}
        map={map}
        onClickOutside={handleClearPosition}
        position={position}
        smartPosition
        zIndex={zIndex}
      >
        <div className={cn(styles.container, { [styles.selected]: isSelected })}>
          <div className={styles.arrow} />
          <div className={styles.border} />

          <Interactive>
            <div className={styles.body}>
              <div className={styles.info}>
                <div className={styles.details}>
                  <div className={styles.name}>{lead.name}</div>
                  <div className={styles.address}>{lead.formatted_address}</div>
                </div>

                <DuplicateWarningIcon entity={lead} onClick={onWarningClick} />
              </div>

              <LeadItemHours openingHours={lead.opening_hours} />
              <LeadFinderItemActions entity={lead} />

              <div className={styles.action}>
                {isSelected ? (
                  <Button block className={styles.btnUndo} onClick={handleUnselect}>
                    {intl.formatMessage(messages.undoSelection)}
                  </Button>
                ) : (
                  <Button block className={styles.btn} onClick={handleSelect}>
                    {intl.formatMessage(messages.selectThisLead)}
                  </Button>
                )}
              </div>
            </div>
          </Interactive>
        </div>
      </OverlayView>
    </AnalyticsContext.Provider>
  ) : null;
};

const mapStateToProps = (state: RootState) => ({
  annotation: getLeadAnnotation(state),
});

const mapDispatchToProps = {
  onSetAnnotation: setLeadAnnotation,
  onWarningClick: showDuplicates,
};

export default connect(mapStateToProps, mapDispatchToProps)(LeadAnnotation);
